import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	AtlBrowserStorageLocal,
	AtlBrowserStorageSession,
} from '@atlassian/browser-storage-controls/web-storage-service';
import { STOREJS_KEY_PREFIX } from './constants.tsx';
import { type ExpirableWebStorageProvider, type SetItemResult, WebStorageType } from './types.tsx';

import { isStorageAPIAvailable } from './isStorageAPIAvailable.tsx';

// Reliability is reported via batched operational analytics rather than a per-call backend log
// POST, which was competing with critical-path requests and regressing VC90. Sentry is skipped to
// keep this off the critical path. The key is included in the payload only when the caller passes
// it (write operations), so it can aid debugging without being reported for every operation.
const logStorageError = (operation: string, error: unknown, key?: string): void => {
	fireErrorAnalytics({
		meta: {
			id: 'storageOperationError',
			packageName: 'jiraBrowserStorageProviders',
			teamName: 'jfp-vulcan',
		},
		attributes: key !== undefined ? { operation, key } : { operation },
		error: error instanceof Error ? error : new Error(`Failed to ${operation}`),
		skipSentry: true,
	});
};

export const createExpirableStorageProvider = (
	storageType: (typeof WebStorageType)[keyof typeof WebStorageType],
	appPrefix: string,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	store: any,
): ExpirableWebStorageProvider => {
	if (!appPrefix) {
		throw Error("appPrefix can not be empty. It's used to separate apps");
	}
	const namespacedStore = store.namespace(appPrefix);
	// Uses the same key format as store.js: '__storejs_' + namespace + '_' + key
	const getKeyWithPrefix = (key: string) => `${STOREJS_KEY_PREFIX}${appPrefix}_${key}`;
	// Uses the same expiration key format as store.js expirePlugin:
	// Sub-store namespace = __storejs_{appPrefix}_expire_mixin
	// Sub-store prefix = __storejs_ + sub-store namespace + _ = __storejs___storejs_{appPrefix}_expire_mixin_
	const getExpireKeyWithPrefix = (key: string) =>
		`${STOREJS_KEY_PREFIX}${STOREJS_KEY_PREFIX}${appPrefix}_expire_mixin_${key}`;
	const storageAPI =
		storageType === WebStorageType.LocalStorage ? AtlBrowserStorageLocal : AtlBrowserStorageSession;

	const remove = (key: string): void => {
		try {
			if (!isStorageAPIAvailable()) {
				return;
			}
			storageAPI.removeItem(getKeyWithPrefix(key));
			storageAPI.removeItem(getExpireKeyWithPrefix(key));
			// Remove from old store as well
			namespacedStore.remove(key);
		} catch (error) {
			logStorageError('remove item', error);
		}
	};

	return {
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		set: (key: string, value: any, expiration?: number): void => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (!isStorageAPIAvailable()) {
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				const expireKey = getExpireKeyWithPrefix(key);
				storageAPI.setItem(prefixedKey, JSON.stringify(value)).then(
					(setItemResult) => {
						if (setItemResult.status !== 'SUCCESS') {
							storageAPI.removeItem(expireKey);
							return;
						}
						// Matches store.js: set(key, val) without expiration clears any existing expiration
						if (expiration !== undefined) {
							storageAPI.setStrictlyNecessaryItem(expireKey, JSON.stringify(expiration));
						} else {
							storageAPI.removeItem(expireKey);
						}
					},
					(error) => {
						logStorageError('set item', error, key);
					},
				);
			} catch (error) {
				logStorageError('set item', error, key);
			}
		},

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setAsync: async (key: string, value: any, expiration?: number): Promise<void> => {
			try {
				if (value === undefined) {
					return remove(key);
				}
				if (!isStorageAPIAvailable()) {
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				const setItemResult: SetItemResult = await storageAPI.setItem(
					prefixedKey,
					JSON.stringify(value),
				);
				if (setItemResult !== undefined) {
					if (setItemResult.status === 'BLOCKED') {
						storageAPI.removeItem(prefixedKey);
						throw new Error('write was blocked by storage or consent preferences.');
					}
					if (setItemResult.status !== 'SUCCESS') {
						throw new Error('write failed');
					}
				}
				const expireKey = getExpireKeyWithPrefix(key);
				if (expiration !== undefined) {
					storageAPI.setStrictlyNecessaryItem(expireKey, JSON.stringify(expiration));
				} else {
					storageAPI.removeItem(expireKey);
				}
			} catch (error) {
				logStorageError('set item', error, key);
				throw error;
			}
		},

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setStrictlyNecessaryItem: (key: string, value: any, expiration?: number): void => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (!isStorageAPIAvailable()) {
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				storageAPI.setStrictlyNecessaryItem(prefixedKey, JSON.stringify(value));
				// Matches store.js: set(key, val) without expiration clears any existing expiration
				const expireKey = getExpireKeyWithPrefix(key);
				if (expiration !== undefined) {
					storageAPI.setStrictlyNecessaryItem(expireKey, JSON.stringify(expiration));
				} else {
					storageAPI.removeItem(expireKey);
				}
			} catch (error) {
				logStorageError('set strictly necessary item', error, key);
			}
		},

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		get: (key: string): any | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					return undefined;
				}
				// Matches store.js expirePlugin: check expiration before reading value
				const expireKey = getExpireKeyWithPrefix(key);
				const expirationStr = storageAPI.getItem(expireKey);
				if (expirationStr) {
					const expiration = JSON.parse(expirationStr);
					if (expiration < Date.now()) {
						// Expired: remove both value and expiration keys
						storageAPI.removeItem(getKeyWithPrefix(key));
						storageAPI.removeItem(expireKey);
						return;
					}
				}
				const prefixedKey = getKeyWithPrefix(key);
				const itemStr = storageAPI.getItem(prefixedKey);
				if (typeof itemStr === 'string') {
					return JSON.parse(itemStr);
				}
			} catch (error) {
				logStorageError('get item', error);
			}
		},
		getExpiration: (key: string): number | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					return undefined;
				}
				// Matches store.js expirePlugin: expirations.get(key) — returns raw timestamp
				const expireKey = getExpireKeyWithPrefix(key);
				const expirationStr = storageAPI.getItem(expireKey);
				if (typeof expirationStr === 'string') {
					return JSON.parse(expirationStr);
				}
				return undefined;
			} catch (error) {
				logStorageError('get expiration for item', error);
				return undefined;
			}
		},

		remove,

		removeExpiredKeys: (): void => {
			try {
				if (!isStorageAPIAvailable()) {
					return;
				}
				const expirePrefix = `${STOREJS_KEY_PREFIX}${STOREJS_KEY_PREFIX}`;
				const expireSuffix = '_expire_mixin_';
				for (let i = storageAPI.length() - 1; i >= 0; i--) {
					const storageKey = storageAPI.key(i);
					const suffixIndex = storageKey ? storageKey.indexOf(expireSuffix) : -1;
					if (storageKey && storageKey.startsWith(expirePrefix) && suffixIndex !== -1) {
						const expirationStr = storageAPI.getItem(storageKey);
						if (expirationStr) {
							const expiration = JSON.parse(expirationStr);
							if (expiration < Date.now()) {
								// Derive the value key: __storejs_ + namespace + _ + originalKey
								const namespace = storageKey.substring(expirePrefix.length, suffixIndex);
								const originalKey = storageKey.substring(suffixIndex + expireSuffix.length);
								const valueKey = `${STOREJS_KEY_PREFIX}${namespace}_${originalKey}`;
								storageAPI.removeItem(valueKey);
								storageAPI.removeItem(storageKey);
							}
						}
					}
				}
				namespacedStore.removeExpiredKeys(); // clean up old keys too
			} catch (error) {
				logStorageError('remove expired keys', error);
			}
		},
		// Clears the complete storage
		clearAll: () => {
			try {
				if (!isStorageAPIAvailable()) {
					return;
				}
				storageAPI.clear();
				// clean up old keys too
				namespacedStore.clearAll();
			} catch (err) {
				logStorageError('clear storage', err);
			}
		},
		length: (): number | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					return 0;
				}
				return storageAPI.length();
			} catch (err) {
				logStorageError('get storage length', err);
				return 0;
			}
		},
		key: (index: number): string | null => {
			try {
				if (!isStorageAPIAvailable()) {
					return null;
				}
				return storageAPI.key(index);
			} catch (err) {
				logStorageError('get key at index', err);
				return null;
			}
		},
	};
};
